import { motion, useReducedMotion } from 'framer-motion'
import skiBikeHouseLogo from '../assets/partnerzy/ski-bike-stronie-partner.png'
import { useT } from '../i18n/t'
import { fadeUp } from '../lib/motion'

const pdsLogo = '/pds-logo-partner.png'
const mdsLogo = '/logo-mds-partner.png'

const partners = [
  {
    href: 'https://przystanekd.pl',
    nameKey: 'landing.partners.pds.name',
    logoAltKey: 'landing.partners.pds.logoAlt',
    logoSrc: pdsLogo,
  },
  {
    href: 'https://miejscadolnegoslaska.pl',
    nameKey: 'landing.partners.mds.name',
    logoAltKey: 'landing.partners.mds.logoAlt',
    logoSrc: mdsLogo,
  },
  {
    href: 'https://skibikehouse.pl/',
    nameKey: 'landing.partners.skiBikeHouse.name',
    logoAltKey: 'landing.partners.skiBikeHouse.logoAlt',
    logoSrc: skiBikeHouseLogo,
  },
] as const

type Partner = (typeof partners)[number]

type PartnerLogoLinkProps = {
  partner: Partner
  embedded?: boolean
  showName?: boolean
  decorative?: boolean
}

function PartnerLogoLink({
  partner,
  embedded = false,
  showName = true,
  decorative = false,
}: PartnerLogoLinkProps) {
  const { t } = useT()

  return (
    <a
      href={partner.href}
      target="_blank"
      rel="noopener noreferrer"
      tabIndex={decorative ? -1 : undefined}
      aria-hidden={decorative ? true : undefined}
      className={
        'group flex shrink-0 flex-col items-center gap-2 rounded-lg transition hover:bg-ink/[0.03] focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/50 ' +
        (embedded
          ? 'max-w-[200px] px-3 py-2 sm:max-w-[220px]'
          : 'w-[220px] gap-3 px-4 py-3 sm:w-[240px] md:w-[260px]')
      }
    >
      <img
        src={partner.logoSrc}
        alt={decorative ? '' : t(partner.logoAltKey)}
        draggable={false}
        loading="lazy"
        decoding="async"
        className="h-auto w-full max-h-[72px] select-none object-contain opacity-90 transition group-hover:opacity-100 md:max-h-[80px]"
      />
      {showName && !embedded ? (
        <span className="text-center text-[13px] font-semibold text-ink/60 transition group-hover:text-ink/80">
          {t(partner.nameKey)}
        </span>
      ) : null}
    </a>
  )
}

function PartnersMarquee({ listAria }: { listAria: string }) {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) {
    return (
      <ul
        aria-label={listAria}
        className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-8 md:mt-10 md:gap-x-14"
      >
        {partners.map((partner) => (
          <li key={partner.href} className="flex justify-center">
            <PartnerLogoLink partner={partner} />
          </li>
        ))}
      </ul>
    )
  }

  const renderRow = (suffix: string, decorative: boolean) =>
    partners.map((partner) => (
      <li key={`${partner.href}${suffix}`} className="flex justify-center">
        <PartnerLogoLink partner={partner} decorative={decorative} />
      </li>
    ))

  return (
    <div className="partners-marquee relative mt-8 md:mt-10">
      <div className="overflow-hidden">
        <ul
          aria-label={listAria}
          className="partners-marquee-track items-center gap-x-10 md:gap-x-14"
        >
          {renderRow('', false)}
          {renderRow('-dup', true)}
        </ul>
      </div>
    </div>
  )
}

type LandingPartnersSectionProps = {
  /** Wąski blok w kolumnie formularza (bez pełnej sekcji landingowej). */
  embedded?: boolean
  /** Bez animacji wejścia przy scrollu. */
  calm?: boolean
}

export function LandingPartnersSection({
  embedded = false,
  calm = false,
}: LandingPartnersSectionProps) {
  const { t } = useT()
  const headingId = embedded ? 'formularz-partners-heading' : 'landing-partners-heading'
  const listAria = t('landing.partners.listAria')

  const logos = embedded ? (
    <ul
      aria-label={listAria}
      className="mt-6 flex flex-row flex-wrap items-center gap-x-12 gap-y-8 sm:gap-x-16"
    >
      {partners.map((partner) => (
        <li key={partner.href} className="flex justify-center">
          <PartnerLogoLink partner={partner} embedded />
        </li>
      ))}
    </ul>
  ) : (
    <PartnersMarquee listAria={listAria} />
  )

  if (embedded) {
    return (
      <div aria-labelledby={headingId} className="mt-8 border-t border-ink/10 pt-8">
        <h2
          id={headingId}
          className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink/55"
        >
          {t('landing.partners.title')}
        </h2>
        {logos}
      </div>
    )
  }

  const title = (
    <h2
      id={headingId}
      className="text-center text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink/55"
    >
      {t('landing.partners.title')}
    </h2>
  )

  if (calm) {
    return (
      <section aria-labelledby={headingId} className="border-t border-ink/10 bg-paper">
        <div className="container-x py-12 md:py-16">
          <div className="content">{title}</div>
          {logos}
        </div>
      </section>
    )
  }

  return (
    <section aria-labelledby={headingId} className="border-t border-ink/10 bg-paper">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
        className="container-x pb-14 md:pb-20"
      >
        <div className="content">
          <motion.h2
            id={headingId}
            variants={fadeUp}
            className="text-center text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink/55"
          >
            {t('landing.partners.title')}
          </motion.h2>
        </div>
        <motion.div variants={fadeUp}>{logos}</motion.div>
      </motion.div>
    </section>
  )
}
